import { ComboboxItem } from '@mantine/core';
import { useMemo, useState } from 'react';

import { defaultValues } from '@/constants/patient-data-form.constants.ts';

import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { EnrichedDCConfig } from '@/models/EnrichedPrismicDocuments.ts';
import { EditPatientFormData, PatientFormData } from '@/models/Patient.ts';
import { PVSProvider, pvs_provider_names } from '@/models/customers/PVSProvider.ts';

import { hasAddressParts } from '@/utils/formUtils.ts';
import { getCountryOptions } from '@/utils/localeUtils.ts';
import { createPracticeOptionsForInstance } from '@/utils/practitioner/cachedDataUtils.ts';

type UsePatientDataFormReturn = {
    enrichedInitialValues: PatientFormData | EditPatientFormData;
    usedConfig: EnrichedDCConfig | undefined;
    instanceIdOptions: ComboboxItem[];
    practiceOptions: ComboboxItem[] | undefined;
    countryOptions: ComboboxItem[];
    isDocCirrusProvider: boolean;
    isAddressRequired: boolean;
    handleInstanceIdSelect: (instance_id: string | undefined) => void;
    handleValuesChange: (values: PatientFormData | EditPatientFormData) => void;
};
export const usePatientDataForm = (
    initialValues: PatientFormData | EditPatientFormData | undefined,
    isEdit: boolean,
    pvsProvider?: PVSProvider
): UsePatientDataFormReturn => {
    const { instance_ids: instanceIds, getInstanceConfig, config_record: configRecord } = useConfig();
    const [config, setConfig] = useState<EnrichedDCConfig | undefined>();
    const { data: enrichedPractitionerData } = useEnrichedPractitionerData();

    const enrichedInitialValues = useMemo<PatientFormData | EditPatientFormData>(() => {
        if (isEdit) {
            if (!initialValues) {
                throw new Error('usePatientDataForm: initialValues are required when isEdit is true');
            }

            return initialValues as EditPatientFormData;
        }

        // Improve default values for booking
        const values: PatientFormData = { ...defaultValues, ...initialValues };

        // Add default instance_id if only one instance is available for customer
        const instanceId: string | undefined = instanceIds.length === 1 ? instanceIds[0] : undefined;
        if (instanceId) {
            values.instance_id = instanceId;

            // Add default location_id if only one practice is available for instance
            const instanceData = enrichedPractitionerData?.find((i) => i.instance_id === instanceId);
            if (instanceData?.practices && instanceData?.practices?.length === 1) {
                values.location_id = instanceData?.practices?.[0]?.pvs?.id ?? '';
            }
        }

        // TODO: add default insurance_type if only one insurance is available for customer

        return values;
    }, [instanceIds, enrichedPractitionerData, initialValues, isEdit]);

    const [selectedInstanceId, setSelectedInstanceId] = useState<string>(
        isEdit ? '' : ((enrichedInitialValues as PatientFormData)?.instance_id ?? '')
    );

    const usedConfig = config ?? (selectedInstanceId ? getInstanceConfig(selectedInstanceId) : undefined);

    // TODO: use for animation
    // enum Step {
    //     PERSONAL_INFO = 'personal info',
    //     ADDRESS = 'address',
    //     CONTACT = 'contact',
    //     COST_SELECTOR = 'cost selector',
    // }

    const instanceIdOptions: ComboboxItem[] = useMemo(
        (): ComboboxItem[] =>
            instanceIds.map((id) => ({
                value: id,
                label: pvs_provider_names[configRecord[id]?.pvs_provider],
            })),
        [instanceIds, configRecord]
    );

    const practiceOptions: ComboboxItem[] | undefined = useMemo(() => {
        if (!selectedInstanceId) return undefined;
        const options = createPracticeOptionsForInstance(enrichedPractitionerData, selectedInstanceId);
        return options;
    }, [selectedInstanceId, enrichedPractitionerData]);

    const countryOptions: ComboboxItem[] = useMemo(() => {
        // Check if the current instance is Datacur - if so, limit to DACH countries only
        const isDatacur = usedConfig?.pvs_provider === PVSProvider.DATA_CUR;
        return getCountryOptions(isDatacur);
    }, [usedConfig?.pvs_provider]);

    // In edit mode the instance isn't re-selected, so rely on the patient's provider passed in;
    // in create mode it derives from the config of the selected instance.
    const isDocCirrusProvider = isEdit
        ? pvsProvider === PVSProvider.DOC_CIRRUS
        : usedConfig?.pvs_provider === PVSProvider.DOC_CIRRUS;

    // Doc Cirrus allows editing a patient without an address, but a new patient, a stored address or any entered
    // address part needs a complete one
    const [hasEnteredAddress, setHasEnteredAddress] = useState(false);
    const hasStoredAddress = isEdit && !!initialValues && hasAddressParts(initialValues);
    const isAddressRequired = !isDocCirrusProvider || !isEdit || hasStoredAddress || hasEnteredAddress;

    const handleValuesChange = (values: PatientFormData | EditPatientFormData): void => {
        setHasEnteredAddress(hasAddressParts(values));
    };

    const handleInstanceIdSelect = (instance_id: string | undefined): void => {
        if (!instance_id) {
            setSelectedInstanceId('');
            setConfig(undefined);
            return;
        }

        setSelectedInstanceId(instance_id);
        const configForInstance = getInstanceConfig(instance_id);
        setConfig(configForInstance);
    };

    return {
        enrichedInitialValues,
        usedConfig,
        instanceIdOptions,
        practiceOptions,
        countryOptions,
        isDocCirrusProvider,
        isAddressRequired,
        handleInstanceIdSelect,
        handleValuesChange,
    };
};
